/**
 * WishlistManager
 * Handles adding/removing products to LocalStorage and updating UI.
 */
class WishlistManager {
    constructor() {
        this.storageKey = 'shopify-wishlist';
        this.migrateData();
        this.lists = this.getStoredLists();
        this.init();

        // Localization
        this.messages = {
            add: 'Producto añadido a tu lista de deseos',
            remove: 'Producto eliminado de tu lista de deseos',
            listCreated: 'Lista creada correctamente',
            listDeleted: 'Lista eliminada',
            listRenamed: 'Nombre actualizado'
        };

        const host = window.location.hostname;
        if (host.includes('newgardenshop.pt')) {
            this.messages = {
                add: 'Produto adicionado à sua lista de desejos',
                remove: 'Produto removido da sua lista de desejos',
                listCreated: 'Lista criada com sucesso',
                listDeleted: 'Lista eliminada',
                listRenamed: 'Nome atualizado'
            };
        } else if (host.includes('newgardenshopping.nl')) {
            this.messages = {
                add: 'Product toegevoegd aan je verlanglijst',
                remove: 'Product verwijderd van je verlanglijst',
                listCreated: 'Lijst succesvol aangemaakt',
                listDeleted: 'Lijst verwijderd',
                listRenamed: 'Naam bijgewerkt'
            };
        }

        // Textos de la tienda (locales store_texts.wishlist_*, ver snippets/mkt-store-config.liquid)
        const storeTexts = window.mktWishlistTexts;
        if (storeTexts && storeTexts.messages) {
            Object.keys(storeTexts.messages).forEach((key) => {
                if (storeTexts.messages[key]) this.messages[key] = storeTexts.messages[key];
            });
        }

        this.toggleWishlist = this.toggleWishlist.bind(this);
    }

    pageText(key, fallback) {
        const page = window.mktWishlistTexts && window.mktWishlistTexts.page;
        return (page && page[key]) || fallback;
    }

    getDefaultListName() {
        if (window.mktWishlistTexts && window.mktWishlistTexts.defaultName) return window.mktWishlistTexts.defaultName;
        const host = window.location.hostname;
        if (host.includes('newgardenshop.pt')) {
            return 'A Minha Lista de Desejos';
        } else if (host.includes('newgardenshopping.nl')) {
            return 'Mijn Verlanglijst';
        } else {
            return 'Mi Lista de Deseos';
        }
    }

    migrateData() {
        const stored = localStorage.getItem(this.storageKey);
        if (!stored) return;

        try {
            const data = JSON.parse(stored);
            // If it's an array of items (old format), migrate to new format
            if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && !data[0].items) {
                const newList = {
                    id: 'default',
                    name: this.getDefaultListName(),
                    items: data
                };
                localStorage.setItem(this.storageKey, JSON.stringify([newList]));
            }
        } catch (e) {
            console.error('Migration error:', e);
        }
    }

    init() {
        if (!this.eventsBound) {
            this.bindEvents();
            this.eventsBound = true;
        }
        this.updateUI();
    }

    getStoredLists() {
        try {
            const stored = localStorage.getItem(this.storageKey);
            return stored ? JSON.parse(stored) : [{ id: 'default', name: this.getDefaultListName(), items: [] }];
        } catch (e) {
            console.error('Error parsing wishlist data, resetting:', e);
            const defaultList = [{ id: 'default', name: this.getDefaultListName(), items: [] }];
            localStorage.setItem(this.storageKey, JSON.stringify(defaultList));
            return defaultList;
        }
    }

    saveLists(lists) {
        localStorage.setItem(this.storageKey, JSON.stringify(lists));
        this.lists = lists;
        this.dispatchUpdate();
    }

    // List Management
    createList(name) {
        const id = 'list_' + Math.random().toString(36).substr(2, 9);
        const newList = { id, name, items: [] };
        const updatedLists = [...this.lists, newList];
        this.saveLists(updatedLists);
        this.showToast(this.messages.listCreated);
        return id;
    }

    deleteList(listId) {
        if (listId === 'default' && this.lists.length === 1) {
            this.lists[0].items = [];
            this.saveLists(this.lists);
            return;
        }
        const updatedLists = this.lists.filter(l => l.id !== listId);
        this.saveLists(updatedLists);
        this.showToast(this.messages.listDeleted);
    }

    renameList(listId, newName) {
        const updatedLists = this.lists.map(l => {
            if (l.id === listId) return { ...l, name: newName };
            return l;
        });
        this.saveLists(updatedLists);
        this.showToast(this.messages.listRenamed);
    }

    getList(listId) {
        return this.lists.find(l => l.id === listId) || this.lists[0];
    }

    // Item Management
    addToWishlist(product, listId = 'default') {
        const lists = this.getStoredLists();
        const listIndex = lists.findIndex(l => l.id === listId);

        if (listIndex === -1) return;

        const list = lists[listIndex];
        if (list.items.some(item => item.id.toString() === product.id.toString())) return;

        list.items.push(product);
        this.saveLists(lists);
        this.showToast(this.messages.add);
    }

    removeFromWishlist(productId, listId) {
        const lists = this.getStoredLists();
        const updatedLists = lists.map(l => {
            if (!listId || l.id === listId) {
                l.items = l.items.filter(item => item.id.toString() !== productId.toString());
            }
            return l;
        });
        this.saveLists(updatedLists);
        this.showToast(this.messages.remove);
    }

    async addAllToCart(listId) {
        const list = this.getList(listId);
        if (!list || !list.items.length) {
            this.showToast(this.pageText('noneToAdd', 'No hay productos para añadir'));
            return;
        }

        const btn = document.getElementById('wishlist-add-all-btn') || document.querySelector('[data-list-id="' + listId + '"]');
        const originalText = btn?.querySelector('.btn-text')?.textContent || btn?.textContent;

        if (btn) {
            btn.classList.add('loading');
            btn.disabled = true;
            if (btn.querySelector('.btn-text')) {
                btn.querySelector('.btn-text').textContent = (window.mktWishlistTexts && window.mktWishlistTexts.page && window.mktWishlistTexts.page.adding) || 'Añadiendo...';
            }
        }

        try {
            // Helper: Obtener variant ID del producto
            const getVariantId = async (item) => {
                if (item.variantId) return parseInt(item.variantId);
                // Si tiene handle, obtener el producto y usar la primera variante disponible
                if (item.handle) {
                    try {
                        const response = await fetch(`/products/${item.handle}.js`);
                        if (response.ok) {
                            const product = await response.json();
                            const availableVariant = product.variants.find(v => v.available) || product.variants[0];
                            return availableVariant ? availableVariant.id : item.id;
                        }
                    } catch (e) {
                    }
                }
                return parseInt(item.id);
            };

            // 1. Resolver todos los Variant IDs en paralelo
            const itemPromises = list.items.map(async (item) => {
                const variantId = await getVariantId(item);
                return {
                    id: variantId,
                    quantity: 1
                };
            });

            const itemsToAdd = await Promise.all(itemPromises);

            // 2. Enviar una única petición `items` al Cart API
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ items: itemsToAdd })
            });

            const result = await response.json();

            if (response.ok) {
                this.showToast(((window.mktWishlistTexts && window.mktWishlistTexts.page && window.mktWishlistTexts.page.addedToCart) || '{count} productos añadidos al carrito').replace('{count}', itemsToAdd.length));

                // Actualizar carrito global
                window.dispatchEvent(new CustomEvent('cart:updated'));
                const cartUpdateEvent = new CustomEvent('cart:refresh', { bubbles: true });
                document.documentElement.dispatchEvent(cartUpdateEvent);
                if (window.refreshCart) window.refreshCart();

                // Abrir drawer
                setTimeout(() => {
                    const cartDrawer = document.querySelector('cart-drawer');
                    if (cartDrawer && typeof cartDrawer.open === 'function') {
                        cartDrawer.open();
                    } else {
                        window.location.href = '/cart';
                    }
                }, 500);
            } else {
                console.error('Batch add error:', result);
                this.showToast(this.pageText('addError', 'Error al añadir algunos productos.') + (result.description ? ' ' + result.description : ''));
            }

        } catch (e) {
            console.error('Add all to cart error:', e);
            this.showToast(this.pageText('requestError', 'Ocurrió un error al procesar la solicitud.'));
        } finally {
            if (btn) {
                btn.classList.remove('loading');
                btn.disabled = false;
                if (btn.querySelector('.btn-text')) {
                    btn.querySelector('.btn-text').textContent = originalText;
                }
            }
        }
    }

    isInWishlist(productId, listId) {
        const list = listId ? this.getList(listId) : null;
        if (list) {
            return list.items.some(item => item.id.toString() === productId.toString());
        }
        return this.lists.some(l => l.items.some(item => item.id.toString() === productId.toString()));
    }

    toggleWishlist(product, listId = 'default') {
        const isIn = this.isInWishlist(product.id, listId);
        if (isIn) {
            this.removeFromWishlist(product.id, listId);
        } else {
            this.addToWishlist(product, listId);
        }
        return !isIn; // Returns true if added, false if removed
    }

    renderSelectionModal(productData) {
        const modal = document.getElementById('wishlist-selection-modal');
        const optionsContainer = document.getElementById('wishlist-selection-options');
        if (!modal || !optionsContainer) return;

        optionsContainer.innerHTML = this.lists.map((list, index) => {
            const isChecked = index === 0 ? 'checked' : '';
            const count = list.items.length;
            return `
                <div class="wishlist-selection-option">
                    <input type="radio" name="wishlist-select" id="list-${list.id}" value="${list.id}" ${isChecked}>
                    <label for="list-${list.id}">${list.name} <span class="count">(${count})</span></label>
                </div>
            `;
        }).join('');

        modal.classList.add('active');

        // Close logic
        const closeBtn = document.getElementById('close-selection-modal');
        const submitBtn = document.getElementById('wishlist-selection-submit');
        const newListBtn = document.getElementById('wishlist-new-list-option');

        const closeModal = () => modal.classList.remove('active');
        closeBtn.onclick = closeModal;
        modal.onclick = (e) => { if (e.target === modal) closeModal(); };

        newListBtn.onclick = () => {
            closeModal();
            this.openManageModal('create');
        };

        submitBtn.onclick = () => {
            const selectedListId = modal.querySelector('input[name="wishlist-select"]:checked').value;
            this.addToWishlist(productData, selectedListId);
            closeModal();
        };
    }

    bindEvents() {
        document.addEventListener('click', (e) => {
            const button = e.target.closest('.wishlist-btn');
            if (button) {
                e.preventDefault();
                e.stopPropagation();

                // Refresh lists to ensure we have the latest count
                this.lists = this.getStoredLists();

                const productData = {
                    id: button.dataset.productId,
                    handle: button.dataset.productHandle,
                    title: button.dataset.productTitle,
                    image: button.dataset.productImage,
                    price: button.dataset.productPrice,
                    url: button.dataset.productUrl
                };

                // User requirement: Only show modal if user has > 1 lists.
                // If only 1 list exists, add/toggle directly.
                if (this.lists.length > 1) {
                    this.renderSelectionModal(productData);
                } else {
                    // Falls back to the 'default' or first list if only one exists
                    const listId = this.lists[0]?.id || 'default';
                    this.toggleWishlist(productData, listId);
                }
            }
        });

        // Bind Add All to Cart button
        // Since the button might be dynamically shown/hidden or on a specific page, 
        // we can delegate or check existence.
        document.addEventListener('click', (e) => {
            if (e.target && e.target.closest('#wishlist-add-all-btn')) {
                // Identify which list is currently viewed. 
                // The view logic in main-wishlist.liquid seems to render *all* lists or a specific detail view.
                // We need to know the 'current' list context.
                // Looking at main-wishlist.liquid, it seems we might need to store the currentListId in a data attribute or global var.
                // For now, let's assume we are viewing the 'active' list if in detail view.

                // If we are on the main page, we might be viewing a specific list or "all" (but all isn't a list).
                // The UI shows lists first. When a user clicks a list, it shows detail.
                // We need to capture that ID.

                // Let's assume the UI code (which we will update) sets a data-list-id on the container or button.
                const btn = e.target.closest('#wishlist-add-all-btn');
                const listId = btn.dataset.listId || 'default';
                this.addAllToCart(listId);
            }
        });
    }

    updateUI() {
        const buttons = document.querySelectorAll('.wishlist-btn');
        buttons.forEach(btn => {
            const pid = btn.dataset.productId;
            if (this.isInWishlist(pid)) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        });

        const counters = document.querySelectorAll('.wishlist-count');
        const totalItems = this.lists.reduce((acc, list) => acc + list.items.length, 0);
        counters.forEach(counter => {
            counter.textContent = totalItems;
            counter.style.display = totalItems > 0 ? 'flex' : 'none';
        });
    }

    dispatchUpdate() {
        this.updateUI();
        window.dispatchEvent(new CustomEvent('wishlist:updated', {
            detail: { lists: this.lists }
        }));
    }

    openManageModal(action, listId = null) {
        const modal = document.getElementById('wishlist-manage-modal');
        const title = document.getElementById('wishlist-manage-modal-title');
        const input = document.getElementById('wishlist-list-name-input');
        const submit = document.getElementById('wishlist-manage-submit');
        const closeBtn = document.getElementById('close-manage-modal');

        if (!modal || !title || !input || !submit) return;

        const translations = window.WishlistTranslations || {};
        title.textContent = action === 'create' 
          ? (translations.new_list || 'Nueva lista') 
          : (translations.rename_list || 'Renombrar lista');
        submit.textContent = action === 'create' 
          ? (translations.create || 'CREAR') 
          : (translations.save || 'GUARDAR');
        input.value = action === 'rename' ? this.getList(listId).name : '';

        modal.classList.add('active');
        input.focus();

        const closeModal = () => modal.classList.remove('active');
        closeBtn.onclick = closeModal;
        modal.onclick = (e) => { if (e.target === modal) closeModal(); };

        submit.onclick = () => {
            const name = input.value.trim();
            if (!name) return;
            if (action === 'create') {
                this.createList(name);
            } else {
                this.renameList(listId, name);
            }
            closeModal();
            // Refresh page list if we are on wishlist page
            if (window.renderWishlist) window.renderWishlist();
        };
    }

    showToast(message) {
        let toast = document.getElementById('wishlist-toast');
        if (!toast) {
            toast = document.createElement('div');
            toast.id = 'wishlist-toast';
            document.body.appendChild(toast);
        }
        toast.textContent = message;
        toast.className = 'show';
        setTimeout(() => { toast.className = toast.className.replace('show', ''); }, 3000);
    }
}

// Iniciar
// Instantiate immediately so window.WishlistManager is available for other scripts
// The init() method will handle DOM readiness internally if we check document.readyState there,
// or we can call init on DOMContentLoaded.
window.WishlistManager = new WishlistManager();

document.addEventListener('DOMContentLoaded', () => {
    // Re-bind events in case of dynamic loading or just to be safe
    if (window.WishlistManager) {
        window.WishlistManager.init();
    }
});
